import { ref } from 'vue';
import { diContainer } from '@/coreContainer/container';
import { IInventoryTemplatePreset, IInventoryTemplatePresetUpsertRequest } from '../domains/entities/InventoryTemplate';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import { INetworkResult } from '@/coreDomainsModule/networkResult';

/**
 * 出力条件プリセット（組織内で共有）を管理するコンポーザブル
 */
export const useInventoryTemplatePresets = () => {
  const presets = ref<IInventoryTemplatePreset[]>([]);
  const loading = ref(false);
  const error = ref<IError | null>(null);

  /**
   * プリセット一覧を取得する
   */
  const fetchPresets = async (): Promise<void> => {
    loading.value = true;
    error.value = null;

    const result = await diContainer.getInventoryTemplatePresetListUseCase.Execute();
    if (result.status.type === 'success') {
      presets.value = result.status.data;
    } else {
      error.value = result.status.error;
    }

    loading.value = false;
  };

  /**
   * プリセットを新規登録する
   * @param request - 登録内容
   */
  const savePreset = async (request: IInventoryTemplatePresetUpsertRequest): Promise<INetworkResult<IInventoryTemplatePreset, IError>> => {
    const result = await diContainer.createInventoryTemplatePresetUseCase.Execute(request);
    if (result.status.type === 'success') {
      presets.value = [result.status.data, ...presets.value];
    }
    return result;
  };

  /**
   * プリセットを更新する
   * @param presetId - 更新対象のプリセットID
   * @param request - 更新内容
   */
  const updatePreset = async (
    presetId: string,
    request: IInventoryTemplatePresetUpsertRequest
  ): Promise<INetworkResult<IInventoryTemplatePreset, IError>> => {
    const result = await diContainer.updateInventoryTemplatePresetUseCase.Execute(presetId, request);
    if (result.status.type === 'success') {
      presets.value = [result.status.data, ...presets.value.filter((preset) => preset.presetId !== presetId)];
    }
    return result;
  };

  /**
   * プリセットを削除する
   * @param presetId - 削除対象のプリセットID
   */
  const deletePreset = async (presetId: string): Promise<INetworkResult<void, IError>> => {
    const result = await diContainer.deleteInventoryTemplatePresetUseCase.Execute(presetId);
    if (result.status.type === 'success') {
      presets.value = presets.value.filter((preset) => preset.presetId !== presetId);
      return { status: { type: 'success', data: undefined } };
    }
    return result;
  };

  return {
    presets,
    loading,
    error,
    fetchPresets,
    savePreset,
    updatePreset,
    deletePreset,
  };
};
